import ModalComponent from "../Modal/Modal"
import React, { useEffect, useState } from "react"
import Calendar from "../Calendar/Calendar"
import Input from "../Input/Input"
import Dropdown from "../Dropdown/Dropdown"
import { getBankAccountsTypes, getPaymentStatuses, updPayment } from "../../features/payment/paymentSlice"
import { useDispatch } from 'react-redux'
import { dateToServer } from "../../utils/date"
import dayjs from "dayjs"
import { showSnackBar } from "../Snackbar/Snackbar"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"

// Did the save actually happen, and say so.
//
// createAsyncThunk with rejectWithValue RESOLVES with a rejected action rather than throwing, so
// the try/catch around the dispatch never fires. This modal used to refresh the table
// unconditionally, which reported success on every failure — an operator moved a payment's date,
// read "saved", and the date had not moved (Oscar, MDL ops, 2026-09-08; John Fernandez,
// 2026-08-29). The endpoint has told the truth since vlg-server!1316; nothing was listening.
//
// Returns whether it succeeded so the caller reads as a statement rather than a side effect.
export const reportEditOutcome = ({ result, showSnackBar, triggerUpdate }) => {
  if (!result?.error) {
    triggerUpdate(true)
    return true
  }

  // The server names the cause — the due date could not be read, the write matched no payment,
  // the date did not land. Show it verbatim; a generic message would send the operator back to
  // guessing, which is what this whole failure mode already cost them.
  const reason = typeof result.payload === 'string' && result.payload
    ? result.payload
    : 'The payment was not updated. Please try again.'

  showSnackBar('error', reason)
  return false
}

const idOf = (value) => (value && typeof value === 'object' ? value._id : value) || ''

// What this edit says about the bank account, which is either "I changed it" or nothing at all.
//
// updatePaymentById runs the NACHA debit-block check ONLY when a request carries a billingAccount,
// and deliberately so: an amount, due-date or status edit on a blocked payment is legitimate work.
// This modal pre-fills the account from the row and posted the whole form back, so a date-only edit
// arrived carrying an account nobody touched, the server read it as a move, and refused — meaning
// no payment on a blocked account could be rescheduled at all (Oscar, MDL ops, 2026-09-08).
//
// Compared by id: one table passes the id as a string, the other passes the populated object.
export const accountChange = ({ current, initial }) => (
  idOf(current) && idOf(current) !== idOf(initial) ? { billingAccount: idOf(current) } : {}
)

export const statusChange = ({ current, initial }) => (
  idOf(current) && idOf(current) !== idOf(initial) ? { status: idOf(current) } : {}
)

const EditPaymentModal = ({ triggerUpdate, curUserId, dataRow, open, onClose }) => {
  const dispatch = useDispatch()
  // const [types, setTypes] = useState([])
  const [status, setStatus] = useState([])
  const [billing, setBilling] = useState([])
  const [disableUpdate, setDisableUpdate] = useState(true)

  const [formData, setFormData] = useState({
    amount: '',
    billingAccount: '',
    transactionId: '',
    // type: '',
    status: '',
    dueDate: ''
  })
  const { triggerLoading } = useMyContextLoading()

  useEffect(() => {
    setFormData({
      dueDate: dataRow?.dueDate ? dayjs.utc(dataRow.dueDate).format('YYYY-MM-DD') : '',
      amount: dataRow?.amount,
      status: dataRow?.statusId,
      transactionId: dataRow?.transactionId,
      billingAccount: dataRow?.billingAccountId
    })
  }, [dataRow])
  const updateFormData = (name, value) => {
    const copyData = { ...formData }
    copyData[name] = value
    setFormData(copyData)
    setDisableUpdate(false)
  }
  useEffect(() => {
    const fetchData = async () => {
      try {
        const statusResult = await dispatch(getPaymentStatuses())
        setStatus(statusResult.payload.data)
      } catch (error) {
        showSnackBar("error", error)
        console.error('An unexpected error occurred while fetching payment status:', error)
      }
      try {
        const billingResult = await dispatch(getBankAccountsTypes(curUserId))
        setBilling(billingResult.payload.data)
      } catch (error) {
        showSnackBar("error", error)
        console.error('An unexpected error occurred while fetching payment status:', error)
      }
    }

    fetchData()
  }, [])

  const updatePayment = async () => {
    triggerLoading(true)
    try {
      const { billingAccount, status: statusId, ...rest } = formData
      const payload = {
        ...rest,
        ...accountChange({ current: billingAccount, initial: dataRow?.billingAccountId }),
        ...statusChange({ current: statusId, initial: dataRow?.statusId }),
        dueDate: dateToServer(formData.dueDate)
      }
      const result = await dispatch(updPayment({ formData: payload, pId: dataRow?.id }))
      reportEditOutcome({ result, showSnackBar, triggerUpdate })
    } catch (error) {
      showSnackBar("error", error)
      console.error('An unexpected error occurred while fetching payment status:', error)
    }
    triggerLoading(false)
  }
  return (
    <ModalComponent disableStyleBtn={disableUpdate} modalButtonTitle="Submit" title="Edit Payment Schedule"
      buttonClasses="border !border-primary-main h-10" buttonType="outlined"
      disableBtn={false} customOpenModal={open} onClose={onClose}
      functionButton={() => updatePayment()}>

      <Calendar
        width="w-full"
        label="Due Date"
        value={formData?.dueDate}
        onChange={(val) => updateFormData('dueDate', val)}
        isRequired={true}
        timezone="system"
      />

      <Input placeholder={"Enter the number of Payments"}
        label="Amount"
        type="Number"
        leftSideIcon={<div className="text-text-secondary">$</div>}
        leftSideIconStyle="ml-5 mt-[17px]"
        value={formData?.amount}
        onChange={(val) => updateFormData('amount', val.target.value)}
        parentClassName="mt-6"
      />

      <Input placeholder={"Enter the number of Payments"}
        parentClassName="mt-6"
        label="Transaction ID"
        value={formData.transactionId}
        onChange={(val) => updateFormData('transactionId', val.target.value)}
      />

      <Dropdown value={formData?.billingAccount} setValue={(val) => updateFormData('billingAccount', val)}
        myListSubheader={[
          { name: 'Bank Accounts', value: 'bankAccounts' },
          { name: 'Credit Cards', value: 'creditCards' }
        ]}
        menuItems={billing}
        className="mt-6" variant="standard" label="Billing Account" placeholder="Select a value" />

      <Dropdown value={formData?.status} setValue={(val) => updateFormData('status', val)}
        menuItems={status}
        className="mt-6" variant="standard" label="Status" placeholder="Select a value" />

    </ModalComponent>
  )
}

export default EditPaymentModal
